import type { UnverifiedEvidence, VerifiedEvidence } from '../../dto/marketplace.js';
import { Card } from '../Card.js';
import { PanelTitle } from '../PanelTitle.js';
import { formatDate } from '../format.js';

/**
 * SIL-1340: renders the tier-tagged `evidence` projection (SIL-1331, spec
 * 5.2) on the customer listing detail page. Mirrors the `priceText` /
 * `pricing.tier` switch in ../../lib/formatPriceRange.ts — the client never
 * derives location or verification state from anything else, it only
 * formats whatever tier the server already decided to send.
 *
 * `evidence` is non-null in the current API contract (evidenceSchema is a
 * discriminated union, never optional) but this component treats a missing
 * value defensively — a stale cached response or an unexpected server
 * change should degrade to "nothing rendered", never a crash or a leaked
 * guess at location.
 */

function BoolBadge({ label, value }: { label: string; value: boolean }) {
  return (
    <span
      className={
        value
          ? 'inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200'
          : 'inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground ring-1 ring-inset ring-border'
      }
    >
      {label}
    </span>
  );
}

function UnverifiedEvidenceBody({ evidence }: { evidence: UnverifiedEvidence }) {
  return (
    <>
      <Card>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-3">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Region</dt>
            <dd className="mt-1 text-sm text-foreground">{evidence.region ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Nodes</dt>
            <dd className="mt-1 text-sm text-foreground">{evidence.nodeCount}</dd>
          </div>
        </dl>
        <div className="mt-4 flex flex-wrap gap-2">
          <BoolBadge label="Active verification" value={evidence.hasActiveVerification} />
          <BoolBadge label="Self-reported" value={evidence.isSelfReported} />
          <BoolBadge label="Custody unbroken" value={evidence.custodyUnbroken} />
        </div>
      </Card>
      <p className="mt-3 text-xs text-muted-foreground">
        Complete buyer verification to see the exact site, the full per-run verification
        breakdown (pass/observed/fail counts, verification tier, run and expiry dates) and the
        chain-of-custody history for every node in this listing.
      </p>
    </>
  );
}

/**
 * SIL-1348: exported so `components/exchange/PassportCard.tsx` can COMPOSE the
 * verified evidence breakdown instead of restating it. The new Passport card
 * owns the headline (pass ratio, AV-state chip, run meta) and the FMV band;
 * the per-run counts and the custody summary below are this component's
 * markup, rendered once, from one place. A second copy of these tables is the
 * thing this export exists to avoid — the numbers would drift the first time
 * one of them gained a field.
 */
export function VerifiedEvidenceBody({ evidence }: { evidence: VerifiedEvidence }) {
  const { verificationSummary: vs, custodySummary: cs } = evidence;
  return (
    <div className="space-y-4">
      <Card>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Site</dt>
            <dd className="mt-1 text-sm text-foreground">{evidence.site ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Region</dt>
            <dd className="mt-1 text-sm text-foreground">{evidence.region ?? '—'}</dd>
          </div>
        </dl>
      </Card>

      <div>
        <PanelTitle title="Verification summary" className="mb-2" />
        <Card>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Pass</dt>
              <dd className="mt-1 text-sm text-foreground">{vs.pass}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Observed</dt>
              <dd className="mt-1 text-sm text-foreground">{vs.obs}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Fail</dt>
              <dd className="mt-1 text-sm text-foreground">{vs.fail}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Nodes</dt>
              <dd className="mt-1 text-sm text-foreground">{vs.nodes}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Unverified nodes
              </dt>
              <dd className="mt-1 text-sm text-foreground">{vs.unverifiedNodes}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Tier</dt>
              <dd className="mt-1 text-sm text-foreground">{vs.tier ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Run at</dt>
              <dd className="mt-1 text-sm text-foreground">{formatDate(vs.runAt)}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Valid to</dt>
              <dd className="mt-1 text-sm text-foreground">{formatDate(vs.validTo)}</dd>
            </div>
          </dl>
        </Card>
      </div>

      <div>
        <PanelTitle title="Custody summary" className="mb-2" />
        <Card>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Events</dt>
              <dd className="mt-1 text-sm text-foreground">{cs.events}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Owners</dt>
              <dd className="mt-1 text-sm text-foreground">{cs.owners}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Custody unbroken
              </dt>
              <dd className="mt-1">
                <BoolBadge label={cs.unbroken ? 'Unbroken' : 'Broken'} value={cs.unbroken} />
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Last event
              </dt>
              <dd className="mt-1 text-sm text-foreground">{formatDate(cs.lastEventAt)}</dd>
            </div>
          </dl>
        </Card>
      </div>
    </div>
  );
}

export function EvidencePanel({
  evidence,
}: {
  evidence: VerifiedEvidence | UnverifiedEvidence | null | undefined;
}) {
  if (evidence == null) return null;

  return (
    <section>
      <PanelTitle title="Evidence" className="mb-3" />
      {evidence.tier === 'verified' ? (
        <VerifiedEvidenceBody evidence={evidence} />
      ) : (
        <UnverifiedEvidenceBody evidence={evidence} />
      )}
    </section>
  );
}
